<template>
    <v-container>
        <statistical-header :title="title"></statistical-header>
        
         <v-select
            v-model="value"
            :items="items"
            attach
            chips
            deletable-chips
            label="Please select positions to display"
            multiple
          ></v-select>

        <v-checkbox
        v-model="showParliaments"
        label="Show Parliaments"
        ></v-checkbox>

          <loader v-if="loading"/>

        <timeline id="timeline" :chartData="chartData" :showParliaments="showParliaments" v-if="!loading"/>
        <br/>
    </v-container>
</template>
<script>
import Timeline from './Timeline.vue';
import axios from 'axios';
window.Handbook = window.Handbook || {}
const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
axios.defaults.baseURL = apiUri;
import orderBy from 'lodash.orderby';
import Loader from '@/components/Loader.vue';
import StatisticalHeader from './StatisticalHeader.vue';

// get data for pms, leaders of the opposition, and format them correctly

const formatLeaders = (items, y) => {
    const data = orderBy(
        items
        .map(i => ({...i, PeriodOfService: i.PeriodOfService.map(p => ({...p, Parliamentarian: i.DisplayName, Phid: i.Phid}))}))
        .flatMap(i => i.PeriodOfService),
        [p => new Date(p.DateStart).valueOf()],
        ['asc']
        )
        .map((p, i) => {
            return {
                name: p.Parliamentarian,
                color: p.PartyColour,
                phid: p.Phid,
                party: p.PartyName,
                x: new Date(p.DateStart).valueOf(),
                present: p.DateEnd === null,
                x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : new Date().valueOf(),
                y
            }
        });
        // need to break up terms of service and get colour
        let newData = [];
        data.forEach(d => {
            // find parliamentarian
            // find party service
            const item = items.find(i => i.Phid === d.phid);
            // replace item with party service
            // if there is only one party service, don't worry about it - assign colour
            if (item.Party.length === 1) {
                d.color = item.Party[0].Colour;
                d.party = item.Party[0].Name
                newData.push(d);
            } else {
                // need to break record up into segments
                // for each party
                // find matching parties
                item.Party.filter(p => (new Date(p.DateStart).valueOf() < d.x2 && new Date(p.DateEnd).valueOf() > d.x)).forEach(p => {
                    // find fully matching segment
                    if (d.x >= new Date(p.DateStart).valueOf() && d.x2 <= new Date(p.DateEnd).valueOf() ) {
                        d.color = p.Colour;
                        d.party = p.Name
                        newData.push(d);
                    } else {
                        const newSegment = {
                            ...d,
                            x: new Date(p.DateStart).valueOf() > d.x ? new Date(p.DateStart).valueOf() : d.x,
                            x2: p.DateEnd && new Date(p.DateEnd).valueOf() < d.x2 ? new Date(p.DateEnd).valueOf() : d.x2, 
                            color: p.Colour,
                            party: p.Name
                        };
                        newData.push(newSegment);
                    }
                });
            }
        });
        newData = newData.map((d, i) => ({...d, dataLabels: {
            formatter: function() {              
                return this.point.shapeArgs.width > 4 ? `<a class="timeline-label-parl" href="/Parliamentarian/${this.point.phid}"><img src="https://www.aph.gov.au/api/parliamentarian/${this.point.phid}/image" style="width: 20px;border-radius: 5px;"></img><div class="ml-1">${this.point.name}</div></a>` : '';
            }
        }}));
        return newData;
}

const formatOfficeHolders = (data, y, colour) => 
    data
        .map(p => ({
            id: p.Id,
            name: p.DisplayName,
            shortName: p.ShortName,
            hasPortrait: p.HasPortrait,
            color: colour, 
            party: null,
            x: new Date(p.DateStart).valueOf(),
            present: p.DateEnd === null,
            x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : new Date().valueOf(),
            y,
            dataLabels: {
                format: p.HasPortrait ? `<a class="timeline-label-parl" a href="/ParliamentaryOfficeHolder/{point.id}"><img src="${axios.defaults.baseURL}api/HistoricalInformation/Portrait?id={point.id}" style="width: 20px;border-radius: 5px;"></img><div class="ml-1">{point.shortName}</div></a>`
                : '<a href="/ParliamentaryOfficeHolder/{point.id}" class="timeline-label">{point.shortName}</a>'                             
            }
        }));

const formatMinistries = (data, y) => 
    data
        .map(p => ({
            id: p.Id,
            name: p.MinistryName,
            color: p.colour,
            party: null,
            leaderPhid: p.LeaderPHID,
            partyList: p.Parties,
            x: new Date(p.DateStart).valueOf(),
            present: p.DateEnd === null,
            x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : new Date().valueOf(),
            y,
            dataLabels: {
                format: '<a href="/Ministry/{point.id}" class="timeline-label">{point.name}</a>',
            }
        }));

export default {
    components: {
        StatisticalHeader,
        Timeline,
        Loader
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue, oldValue) {                
                newValue.forEach(v => {
                    if (v === 'Prime Ministers' && this.primeMinisters.length === 0) {
                        axios.get(`api/StatisticalInformation/PrimeMinisters`)
                            .then(response => {
                                this.primeMinisters = response.data;
                                this.loading = false;
                            });                    
                    } else if (v === 'Ministries' && this.ministries.length === 0) {
                        axios.get(`api/StatisticalInformation/Ministries`)
                            .then(response => {
                                this.ministries = response.data;
                            });                    
                    } else if (v === 'Leaders of the Opposition' && this.leadersOfTheOpposition.length === 0) {
                        axios.get(`api/StatisticalInformation/LeadersOfTheOpposition`)
                            .then(response => {
                                this.leadersOfTheOpposition = response.data;
                            });                    
                    } else if (v === 'Speakers of the House of Representatives' && this.speakers.length === 0) {
                        axios.get(`api/StatisticalInformation/Speakers`)
                            .then(response => {
                                this.speakers = response.data;
                            });                    
                    } else if (v === 'Presidents of the Senate' && this.presidents.length === 0) {
                        axios.get(`api/StatisticalInformation/Presidents`)
                            .then(response => {
                                this.presidents = response.data;
                            });                    
                    } else if (v === 'Clerks of the Senate' && this.clerksSenate.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=1')
                            .then(response => {
                                this.clerksSenate = response.data;
                            });               
                    } else if (v === 'Clerks of the House of Representatives' && this.clerksReps.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=2')
                            .then(response => {
                                this.clerksReps = response.data;
                            });               
                    } else if (v === 'Principal Parliamentary Reporters' && this.parliamentaryReporters.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=7')
                            .then(response => {
                                this.parliamentaryReporters = response.data;
                            });              
                    } else if (v === 'Joint House Department Secretaries' && this.jointHouseSecretaries.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=8')
                            .then(response => {
                                this.jointHouseSecretaries = response.data;
                            });                    
                    } else if (v === 'Department of Parliamentary Services Secretaries' && this.parliamentaryServiceSecretaries.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=3')
                            .then(response => {
                                this.parliamentaryServiceSecretaries = response.data;
                            });
                    } else if (v === 'Parliamentary Librarians' && this.parliamentaryLibrarians.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=4')
                            .then(response => {
                                this.parliamentaryLibrarians = response.data;
                            });
                    } else if (v === 'Parliamentary Budget Officers' && this.parliamentaryBudgetOfficers.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=5')
                            .then(response => {
                                this.parliamentaryBudgetOfficers = response.data;
                            });
                    } else if (v === 'Monarchs' && this.monarchs.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=9')
                            .then(response => {
                                this.monarchs = response.data;
                            });                                                                      
                    } else if (v === 'Governors-General' && this.governorsGeneral.length === 0) {
                        axios.get('api/HistoricalInformation/OfficeHolders?officeType=6')
                            .then(response => {
                                this.governorsGeneral = response.data;
                            });
                    }                                        
                });
            }                    
        }
    },
    computed: {
        chartData() {
            // need refactor
            const formattedItems = [...this.primeMinisters].map(item => {
                // need to assign 
                const newData = [];
                item.PeriodOfService.forEach(d => {
                    // find party
                    if (item.Party.length === 1) {
                        // d.PartyColour = item.Party[0].Colour;
                        // d.PartyName = item.Party[0].Name
                        newData.push({
                            ...d,
                            PartyColour: item.Party[0].Colour,
                            PartyName: item.Party[0].Name,
                            TotalDays: Math.floor(((d.DateEnd ? new Date(d.DateEnd) : new Date()) - new Date(d.DateStart)) / 86400000)
                        });
                    } else {
                        // need to break record up into segments
                        // for each party
                        // find matching parties
                        item.Party.filter(p => (new Date(p.DateStart) <= new Date(d.DateEnd) && new Date(p.DateEnd) >= new Date(d.DateStart))).forEach(p => {
                            // find fully matching segment
                            if (new Date(d.DateStart) >= new Date(p.DateStart).valueOf() && new Date(d.DateEnd) <= new Date(p.DateEnd)) {
                                // d.PartyColour = p.Colour;
                                // d.PartyName = p.Name
                                newData.push({
                                    ...d,
                                    PartyColour: p.Colour,
                                    PartyName: p.Name,
                                    TotalDays: Math.floor(((d.DateEnd ? new Date(d.DateEnd) : new Date()) - new Date(d.DateStart)) / 86400000)
                                });
                            } else {
                                const newSegment = {
                                    ...d,
                                    DateStart: new Date(p.DateStart) >= new Date(d.DateStart) ? p.DateStart : d.DateStart,
                                    DateEnd: p.DateEnd && new Date(p.DateEnd) <= new Date(d.DateEnd) ? p.DateEnd : d.DateEnd, 
                                    PartyColour: p.Colour,
                                    PartyName: p.Name,
                                };
                                newSegment.TotalDays = Math.floor(((newSegment.DateEnd ? new Date(newSegment.DateEnd) : new Date()) - new Date(newSegment.DateStart)) / 86400000)
                                newData.push(newSegment);
                            }
                        });
                    }
                });

                return {
                    ...item,
                    PeriodOfService: newData,
                }
            });

            const primeMinisters = formattedItems.flatMap(p => p.PeriodOfService);
            // add colours to ministries based on PMs
            const ministries = this.ministries.map(m => {
                // find a term based on ministry start date
                let colour = ''
                let pm = primeMinisters.find(p => new Date(p.DateStart) <= new Date(m.DateStart) && new Date(p.DateEnd).addDays(1) >= new Date(m.DateEnd));
                if (pm) {
                    colour = pm.PartyColour
                }
                else {
                    pm = primeMinisters.find(p => p.DateStart === m.DateStart);
                    if (pm) {
                        colour = pm.PartyColour
                        // colour = 'pink'
                    }
                }
                return {...m, colour};
            });
            let categories = [];
            let data = [];
            let i = 0;
            if (this.value.includes('Prime Ministers')) {
                data = [...data, ...formatLeaders(this.primeMinisters, i)];
                categories.push('Prime Ministers');
                i++
            }
            if (this.value.includes('Ministries')) {
                data = [...data, ...formatMinistries(ministries, i)];
                categories.push('Ministries');
                i++
            }            
            if (this.value.includes('Leaders of the Opposition'))
            {
                data = [...data, ...formatLeaders(this.leadersOfTheOpposition, i)];
                categories.push('Leaders of the Opposition');
                i++
            }
            if (this.value.includes('Speakers of the House of Representatives'))
            {
                data = [...data, ...formatLeaders(this.speakers, i)];
                categories.push('Speakers of the House of Representatives');
                i++
            }
            if (this.value.includes('Presidents of the Senate'))
            {
                data = [...data, ...formatLeaders(this.presidents, i)];
                categories.push('Presidents of the Senate');
                i++;
            }                                                                            
            if (this.value.includes('Clerks of the Senate'))
            {
                data = [...data, ...formatOfficeHolders(this.clerksSenate, i, '#ab112d')];
                categories.push('Clerks of the Senate');
                i++;
            }
            if (this.value.includes('Clerks of the House of Representatives'))
            {
                data = [...data, ...formatOfficeHolders(this.clerksReps, i, 'rgb(70, 118, 100)')];
                categories.push('Clerks of the House of Representatives');
                i++;
            }
            if (this.value.includes('Principal Parliamentary Reporters'))
            {
                data = [...data, ...formatOfficeHolders(this.parliamentaryReporters, i, '#005FA8')];
                categories.push('Principal Parliamentary Reporters');
                i++;
            }
            if (this.value.includes('Joint House Department Secretaries'))
            {
                data = [...data, ...formatOfficeHolders(this.jointHouseSecretaries, i, '#235275')];
                categories.push('Joint House Department Secretaries');
                i++;
            }         
            if (this.value.includes('Department of Parliamentary Services Secretaries'))
            {
                data = [...data, ...formatOfficeHolders(this.parliamentaryServiceSecretaries, i, '#00335b')];
                categories.push('Department of Parliamentary Services Secretaries');
                i++;
            }
            if (this.value.includes('Parliamentary Librarians'))
            {
                data = [...data, ...formatOfficeHolders(this.parliamentaryLibrarians, i, '#a07dbc')];
                categories.push('Parliamentary Librarians');
                i++;
            } 
            if (this.value.includes('Parliamentary Budget Officers'))
            {
                data = [...data, ...formatOfficeHolders(this.parliamentaryBudgetOfficers, i, '#008BF5')];
                categories.push('Parliamentary Budget Officers');
                i++;
            }
            if (this.value.includes('Monarchs'))
            {
                data = [...data, ...formatOfficeHolders(this.monarchs, i, '#233580')];
                categories.push('Monarchs');
                i++;
            }
            if (this.value.includes('Governors-General'))
            {
                data = [...data, ...formatOfficeHolders(this.governorsGeneral, i, '#dec362')];
                categories.push('Governors-General');
                i++;
            }

            return {
                chart: {
                    height: 9000,
                    type: 'xrange',
                    inverted: true                                     
                },
                 tooltip: {
                        borderWidth: 0,
                        backgroundColor: 'transparent',
                        shadow: false,
                        useHTML: true,
                        followPointer: true,
                        formatter: function () {
                            var todaysDate = new Date();
                            // console.log(this.point)
                            var dateIsToday = new Date(this.x2).setHours(0,0,0,0) == todaysDate.setHours(0,0,0,0);
                            var showEndDate = dateIsToday ? 'present' : new Date(this.x2).toLocaleDateString('en-AU');

                            var partyStrings = this.point.partyList != null ? this.point.partyList.map(p => {
                                let partyString = `<div>
                                    <span tabindex="-1" class="v-chip v-chip--disabled v-chip--small">
                                        <span class="v-chip__content">
                                            <div class="v-avatar" style="height: 48px; width: 48px; background-color: ${p.Colour}; border-color: ${p.Colour};">
                                                <span style="color: ${ (p.Colour == null || p.Colour.toLowerCase() != '#bfff00' ? '#fff' : '#000')} ;">${p.Count}</span>
                                            </div>
                                            <span style="white-space: break-spaces">${p.Name}</span>
                                        </span>
                                    </span>
                                </div>`;

                                return partyString;
                            }).join(' ') : '';

                            let tooltipMarkup = '';
                            if (this.point.phid || this.point.leaderPhid) {
                                tooltipMarkup = '<div class="tooltip" style="width:300px;min-height: 100px;">'
                                + '<div style="display:inline-block;"><img src="https://www.aph.gov.au/api/parliamentarian/' 
                                + (this.point.phid != null ? this.point.phid : this.point.leaderPhid)
                                + '/image" style="width: 80px;border-radius: 5px;" /></div><div style="vertical-align: top;display:inline-block; width:200px; padding-left:10px;">' 
                                + '<p style="font-size:1.1em;font-weight:bold;white-space: normal;">' + this.point.yCategory + '</p>' 
                                + '<p style="font-size:1.1em;white-space: normal;">' + this.point.name + '</p>' 
                                +  (this.point.phid != null ?  '<p style="white-space: normal; margin-bottom:0.2rem;">' + this.point.party + '</p>' : '')
                                + '<p style="white-space: normal; margin-bottom:0.2rem;">' + new Date(this.x).toLocaleDateString('en-AU') + ' - ' + showEndDate + '</p>'
                                + '</div>'
                                + ((this.point.phid == null && this.point.leaderPhid != null) ? '<p style="font-size:0.95em; margin-bottom:0rem;font-weight:bold;white-space: normal;">Ministers:</p>'  : '')
                                + (this.point.phid == null ? partyStrings : '')
                                + '</div>';                                
                            } else if (this.point.hasPortrait) {
                                tooltipMarkup = '<div class="tooltip" style="width:300px;min-height: 100px;">'
                                + `<div style="display:inline-block;"><img src="${axios.defaults.baseURL}api/HistoricalInformation/Portrait?id=` 
                                + this.point.id
                                + '" style="width: 80px;border-radius: 5px;" /></div><div style="vertical-align: top;display:inline-block; width:200px; padding-left:10px;">' 
                                + '<p style="font-size:1.1em;font-weight:bold;white-space: normal;">' + this.point.yCategory + '</p>' 
                                + '<p style="font-size:1.1em;white-space: normal;">' + this.point.name + '</p>' 
                                +  (this.point.phid != null ?  '<p style="white-space: normal; margin-bottom:0.2rem;">' + this.point.party + '</p>' : '')
                                + '<p style="white-space: normal; margin-bottom:0.2rem;">' + new Date(this.x).toLocaleDateString('en-AU') + ' - ' + showEndDate + '</p>'
                                + '</div>'
                                + ((this.point.phid == null && this.point.leaderPhid != null) ? '<p style="font-size:0.95em; margin-bottom:0rem;font-weight:bold;white-space: normal;">Ministers:</p>'  : '')
                                + (this.point.phid == null ? partyStrings : '')
                                + '</div>';                                
                            } else {
                                tooltipMarkup = '<div class="tooltip" style="width: 200px;min-height: 100px;">'
                                + '<div style="vertical-align: top;display:inline-block; padding-left:10px;">' 
                                + '<p style="font-size:1.1em;font-weight:bold;white-space: normal;">' + this.point.yCategory + '</p>' 
                                + '<p style="font-size:1.1em;white-space: normal;">' + this.point.name + '</p>' 
                                +  (this.point.phid != null ?  '<p style="white-space: normal; margin-bottom:0.2rem;">' + this.point.party + '</p>' : '')
                                + '<p style="white-space: normal; margin-bottom:0.2rem;">' + new Date(this.x).toLocaleDateString('en-AU') + ' - ' + showEndDate + '</p>'
                                + '</div>'
                                + ((this.point.phid == null && this.point.leaderPhid != null) ? '<p style="font-size:0.95em; margin-bottom:0rem;font-weight:bold;white-space: normal;">Ministers:</p>'  : '')
                                + (this.point.phid == null ? partyStrings : '')
                                + '</div>';
                            }

                            return tooltipMarkup;
                        }, 
                    },
                series: [
                    {
                        pointWidth: 30,
                        data,
                        dataLabels: {
                            enabled: true,
                            useHTML: true,
                            crop: false
                        }
                    },
                ],
                exporting: {
                    enabled: false,
                    sourceWidth: 2000
                },
                yAxis: {
                    title: {
                        text: ''
                    },
                    categories,
                    labels: {
                        y: -8930
                    }
                },
            }
        }
    },
    data() {
        return {
            title: 'Parliamentary timeline',
            items: ['Prime Ministers', 
                'Ministries', 
                'Leaders of the Opposition', 
                'Speakers of the House of Representatives', 
                'Presidents of the Senate',
                'Clerks of the Senate',
                'Clerks of the House of Representatives',
                'Principal Parliamentary Reporters',
                'Joint House Department Secretaries',
                'Parliamentary Librarians',
                'Parliamentary Budget Officers',
                'Monarchs',
                'Governors-General'
                ],
            showParliaments: true,
            value: ['Prime Ministers'],            
            primeMinisters: [],
            ministries: [],
            leadersOfTheOpposition: [],
            speakers: [],
            presidents: [],
            clerksSenate: [],
            clerksReps: [],
            parliamentaryReporters: [],
            jointHouseSecretaries: [],
            parliamentaryServiceSecretaries: [],
            parliamentaryLibrarians: [],
            parliamentaryBudgetOfficers: [],        
            monarchs: [],
            governorsGeneral: [],
            loading: true
        }
    }
}
</script>
<style lang="stylus" scoped>
    .v-label.theme--light {
        color: #000 !important;
    }

    #timeline >>> div {
        overflow: visible !important;
    }

    #timeline >>> .highcharts-container {
        overflow: visible !important;
    }

    #timeline >>> .highcharts-container svg {
        overflow: visible !important;
    }

    #timeline >>> .highcharts-tooltip span {
        background-color: transparent;
    }

    #timeline >>> .highcharts-yaxis-labels text {
        cursor: pointer !important;
    }

    #timeline >>> .timeline-label {
        text-decoration: none; color: black;
    }

    #timeline >>> .timeline-label-parl {
        text-decoration: none; color: black; display: flex; align-items: center;
    }

    #timeline >>> .timeline-label-parl:hover {
        text-decoration: underline;
    }

</style>